import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { supabase } from "@/store/shared/supabase";
import { dispatchDataError, describeSupabaseError } from "@/store/shared/dataErrors";
import { useSession } from "@/session/SessionProvider";
import { fullName } from "@/session/types";
import type {
  AdministrateurStatut,
  PlatformAdministrateur,
  PlatformAdministrateurInput,
} from "./types";

interface AdministratorsContextValue {
  administrateurs: PlatformAdministrateur[];
  chargement: boolean;
  erreur: string | null;
  recharger: () => Promise<void>;
  updateAdministrateur: (id: string, input: PlatformAdministrateurInput) => void;
  changeStatut: (id: string, statut: AdministrateurStatut) => void;
  getAdministrateur: (id: string) => PlatformAdministrateur | undefined;
  /** Nombre de super administrateurs actifs (protection du dernier accès). */
  countSuperAdminsActifs: () => number;
}

const AdministratorsContext = createContext<AdministratorsContextValue | null>(null);

type Row = Record<string, unknown>;
const str = (value: unknown): string => (typeof value === "string" ? value : "");

/**
 * Administrateurs de la plateforme — branchés sur la table RÉELLE `comptes`
 * (comptes dont le rôle est `super_admin`).
 *
 * Lecture réelle via Supabase. Les mises à jour de statut / d'identité sont
 * persistées. La création d'un compte administrateur nécessite un compte Auth
 * (service role) : elle est donc soumise à la sécurité serveur et, si elle est
 * refusée, l'échec est remonté explicitement — jamais masqué.
 */
function rowToAdministrateur(row: Row): PlatformAdministrateur {
  const full = str(row.nom_complet).trim();
  const parts = full.split(/\s+/).filter(Boolean);
  const prenom = parts[0] ?? "";
  const nom = parts.slice(1).join(" ");
  const statut = str(row.statut);
  return {
    id: str(row.id),
    prenom,
    nom,
    email: str(row.email),
    telephone: str(row.telephone),
    role: "super_admin",
    statut: statut === "actif" ? "actif" : "desactive",
    notes: "",
    createdAt: str(row.created_at),
    derniereActivite: str(row.updated_at) || str(row.created_at),
  };
}

export function AdministratorsProvider({ children }: { children: ReactNode }) {
  const { session } = useSession();
  const [administrateurs, setAdministrateurs] = useState<PlatformAdministrateur[]>([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  /** Journalise une action administrative sensible (journal_audit). */
  const logAudit = useCallback(
    (compteId: string, action: string) => {
      void supabase.from("journal_audit").insert({
        compte_id: compteId,
        action,
        table_cible: "comptes",
        nouvelle_valeur: { administrateur: session ? fullName(session) : "Super Admin" },
      });
    },
    [session]
  );

  const recharger = useCallback(async () => {
    if (!session || session.role !== "super_admin") {
      setAdministrateurs([]);
      return;
    }
    setChargement(true);
    setErreur(null);
    try {
      const { data, error } = await supabase
        .from("comptes")
        .select("id, nom_complet, email, telephone, role, statut, created_at, updated_at")
        .eq("role", "super_admin");
      if (error) throw error;
      setAdministrateurs(((data as Row[] | null) ?? []).map(rowToAdministrateur));
    } catch (caught) {
      setErreur("Le chargement des administrateurs a échoué.");
      dispatchDataError({
        scope: "Administrateurs",
        message: "Impossible de charger les administrateurs de la plateforme.",
        technical: describeSupabaseError(caught),
      });
      console.error("[Administrateurs] chargement Supabase échoué", caught);
    } finally {
      setChargement(false);
    }
  }, [session]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  const updateAdministrateur = useCallback(
    (id: string, input: PlatformAdministrateurInput) => {
      const previous = administrateurs.find((item) => item.id === id);
      setAdministrateurs((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, ...input, derniereActivite: new Date().toISOString() } : item
        )
      );
      void supabase
        .from("comptes")
        .update({
          nom_complet: `${input.prenom} ${input.nom}`.trim(),
          email: input.email,
          telephone: input.telephone || null,
          statut: input.statut === "actif" ? "actif" : "desactive",
        })
        .eq("id", id)
        .then(({ error }) => {
          if (error) {
            if (previous) setAdministrateurs((prev) => prev.map((item) => (item.id === id ? previous : item)));
            dispatchDataError({
              scope: "Administrateurs",
              message: "La modification de l'administrateur a échoué.",
              technical: describeSupabaseError(error),
            });
          } else {
            logAudit(id, "Modification administrateur");
            void recharger();
          }
        });
    },
    [administrateurs, recharger, logAudit]
  );

  const changeStatut = useCallback(
    (id: string, statut: AdministrateurStatut) => {
      const previous = administrateurs.find((item) => item.id === id);
      setAdministrateurs((prev) =>
        prev.map((item) => (item.id === id ? { ...item, statut, derniereActivite: new Date().toISOString() } : item))
      );
      void supabase
        .from("comptes")
        .update({ statut: statut === "actif" ? "actif" : "desactive", actif: statut === "actif" })
        .eq("id", id)
        .then(({ error }) => {
          if (error) {
            if (previous) setAdministrateurs((prev) => prev.map((item) => (item.id === id ? previous : item)));
            dispatchDataError({
              scope: "Administrateurs",
              message: "Le changement de statut de l'administrateur a échoué.",
              technical: describeSupabaseError(error),
            });
          } else {
            logAudit(id, statut === "actif" ? "Activation administrateur" : "Désactivation administrateur");
            void recharger();
          }
        });
    },
    [administrateurs, recharger, logAudit]
  );

  const getAdministrateur = useCallback(
    (id: string) => administrateurs.find((item) => item.id === id),
    [administrateurs]
  );

  const countSuperAdminsActifs = useCallback(
    () => administrateurs.filter((item) => item.statut === "actif").length,
    [administrateurs]
  );

  const value = useMemo(
    () => ({
      administrateurs,
      chargement,
      erreur,
      recharger,
      updateAdministrateur,
      changeStatut,
      getAdministrateur,
      countSuperAdminsActifs,
    }),
    [
      administrateurs,
      chargement,
      erreur,
      recharger,
      updateAdministrateur,
      changeStatut,
      getAdministrateur,
      countSuperAdminsActifs,
    ]
  );

  return <AdministratorsContext.Provider value={value}>{children}</AdministratorsContext.Provider>;
}

export function useAdministrators(): AdministratorsContextValue {
  const context = useContext(AdministratorsContext);
  if (!context) {
    throw new Error("useAdministrators doit être utilisé à l'intérieur de AdministratorsProvider");
  }
  return context;
}